import {
  defaultProductRegistryPageSize,
} from '@/config/productRegistry';
import {
  registryProductTotalMock,
  registryProductsMock,
} from '@/data/productRegistryMockData';
import { productService } from '@/services/productService';
import { mapProductToRegistry } from '@/utils/mapToRegistryProduct';
import type {
  ProductRegistryQueryParams,
  ProductRegistryResult,
  RegistryProduct,
} from '@/types/productRegistry';

const mockDelayMs = 250;

function delay<T>(value: T, ms = mockDelayMs): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), ms);
  });
}

async function loadRegistryProducts(): Promise<RegistryProduct[]> {
  try {
    const apiProducts = await productService.getProducts();

    if (apiProducts.length > 0) {
      const mockById = new Map(registryProductsMock.map((item) => [item.id, item]));

      return apiProducts.map((product) => {
        const mockMatch = mockById.get(product.id);

        if (mockMatch) {
          return {
            ...mockMatch,
            name: product.name,
            iframeUrl: product.iframeUrl,
            icon: product.icon ?? mockMatch.icon,
            status: product.isActive === false ? 'inactive' : mockMatch.status,
          };
        }

        return mapProductToRegistry(product);
      });
    }
  } catch {
    // Fall back to mock registry data when API is unavailable.
  }

  return registryProductsMock;
}

function filterProducts(
  products: RegistryProduct[],
  params: ProductRegistryQueryParams,
): RegistryProduct[] {
  const search = params.search?.trim().toLowerCase() ?? '';
  const status = params.status ?? 'all';
  const category = params.category ?? 'all';

  return products.filter((product) => {
    if (status !== 'all' && product.status !== status) {
      return false;
    }

    if (category !== 'all' && product.category !== category) {
      return false;
    }

    if (!search) {
      return true;
    }

    return (
      product.id.toLowerCase().includes(search) ||
      product.name.toLowerCase().includes(search) ||
      product.category.toLowerCase().includes(search)
    );
  });
}

function sortProducts(
  products: RegistryProduct[],
  params: ProductRegistryQueryParams,
): RegistryProduct[] {
  const sortBy = params.sortBy ?? 'lastModified';
  const direction = params.sortDirection ?? 'desc';

  return [...products].sort((left, right) => {
    const leftValue = left[sortBy];
    const rightValue = right[sortBy];

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    }

    const leftString = String(leftValue ?? '');
    const rightString = String(rightValue ?? '');

    return direction === 'asc'
      ? leftString.localeCompare(rightString)
      : rightString.localeCompare(leftString);
  });
}

export async function fetchRegistryProducts(
  params: ProductRegistryQueryParams = {},
): Promise<ProductRegistryResult> {
  // TODO: GET /products with server-side query params
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? defaultProductRegistryPageSize;

  const allProducts = await delay(await loadRegistryProducts());
  const filtered = sortProducts(filterProducts(allProducts, params), params);
  const total = filtered.length > allProducts.length ? filtered.length : registryProductTotalMock;
  const startIndex = (page - 1) * pageSize;
  const items = filtered.slice(startIndex, startIndex + pageSize);

  return {
    items,
    total: Math.max(total, filtered.length),
    page,
    pageSize,
  };
}

export async function fetchProductCategories(): Promise<string[]> {
  // TODO: GET /products/categories
  const products = await loadRegistryProducts();
  return [...new Set(products.map((product) => product.category))];
}

export async function fetchProductEnvironments(): Promise<string[]> {
  // TODO: GET /products/environments
  return ['production', 'stage', 'sandbox', 'development'];
}

export async function fetchProductStatuses(): Promise<string[]> {
  // TODO: GET /products/status
  return ['active', 'inactive', 'maintenance', 'deprecated', 'provisioning', 'error'];
}

export async function createRegistryProduct(): Promise<void> {
  // TODO: POST /products
}

export async function updateRegistryProduct(): Promise<void> {
  // TODO: PUT /products/{id}
}

export async function deleteRegistryProduct(): Promise<void> {
  // TODO: DELETE /products/{id}
}
